import {
	ArrowDownIcon,
	ArrowUpIcon,
	CreditCard,
	Wallet,
	Smartphone,
	MessageSquare,
	UserCheck,
} from 'lucide-react'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/common/ui/card'
import { Separator } from '@/common/ui/separator'
import Spinner from '@/common/components/Spinner'
import useQueryTransactionStatistics from '../../api/hooks/useQueryTransactionStatistics'
import { formatCurrency } from '@/common/lib/utils'
import type { DepositStatistics, WithdrawStatistics } from '../../types'

interface TransactionStatisticsListingProps {
	startDate?: string
	endDate?: string
}

function StatCard({
	title,
	amount,
	count,
	icon,
	colorClass,
}: {
	title: string
	amount: number
	count: number
	icon: React.ReactNode
	colorClass?: string
}) {
	return (
		<div className="group relative overflow-hidden rounded-lg border bg-card p-4 text-center transition-all hover:shadow-md">
			<div className="absolute inset-0 bg-gradient-to-br from-transparent to-muted/20 opacity-0 transition-opacity group-hover:opacity-100" />
			<div className="relative">
				<div className="mb-3 flex items-center justify-center">
					<div
						className={`flex h-10 w-10 items-center justify-center rounded-full ${colorClass || 'bg-primary/10 text-primary'}`}
					>
						{icon}
					</div>
				</div>
				<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
					{title}
				</p>
				<p className="mb-1 text-xl font-bold">{formatCurrency(amount)}</p>
				<p className="text-xs font-medium text-muted-foreground">
					{count.toLocaleString('th-TH')} รายการ
				</p>
			</div>
		</div>
	)
}

function TransactionTypeSection({
	title,
	data,
	icon,
}: {
	title: string
	data: DepositStatistics | WithdrawStatistics
	icon: React.ReactNode
}) {
	const totalAmount =
		data.bank.totalAmount +
		data.paymentGateway.totalAmount +
		data.trueMoney.totalAmount +
		('smsAuto' in data ? data.smsAuto.totalAmount : 0) +
		('manual' in data ? data.manual.totalAmount : 0)

	const totalCount =
		data.bank.totalCount +
		data.paymentGateway.totalCount +
		data.trueMoney.totalCount +
		('smsAuto' in data ? data.smsAuto.totalCount : 0) +
		('manual' in data ? data.manual.totalCount : 0)

	return (
		<Card className="overflow-hidden">
			<CardHeader className="border-b bg-gradient-to-r from-background to-muted/20 pb-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<div
							className={`flex h-12 w-12 items-center justify-center rounded-xl ${
								title === 'รายการฝากเงิน'
									? 'bg-green-500/10 text-green-600'
									: 'bg-red-500/10 text-red-600'
							}`}
						>
							{icon}
						</div>
						<div>
							<CardTitle className="text-lg font-bold">{title}</CardTitle>
							<CardDescription className="mt-1 text-sm font-medium">
								ยอดรวม{' '}
								<span className="font-bold text-foreground">
									{formatCurrency(totalAmount)}
								</span>{' '}
								• {totalCount.toLocaleString('th-TH')} รายการ
							</CardDescription>
						</div>
					</div>
				</div>
			</CardHeader>
			<CardContent className="p-6">
				<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
					<StatCard
						title="ธนาคาร"
						amount={data.bank.totalAmount}
						count={data.bank.totalCount}
						icon={<CreditCard className="h-5 w-5" />}
						colorClass="bg-blue-500/10 text-blue-600"
					/>
					<StatCard
						title="ธนาคารออโต้"
						amount={data.paymentGateway.totalAmount}
						count={data.paymentGateway.totalCount}
						icon={<Wallet className="h-5 w-5" />}
						colorClass="bg-purple-500/10 text-purple-600"
					/>
					<StatCard
						title="ทรูมันนี่"
						amount={data.trueMoney.totalAmount}
						count={data.trueMoney.totalCount}
						icon={<Smartphone className="h-5 w-5" />}
						colorClass="bg-orange-500/10 text-orange-600"
					/>
					{'smsAuto' in data && (
						<StatCard
							title="SMS อัตโนมัติ"
							amount={data.smsAuto.totalAmount}
							count={data.smsAuto.totalCount}
							icon={<MessageSquare className="h-5 w-5" />}
							colorClass="bg-cyan-500/10 text-cyan-600"
						/>
					)}
					{'manual' in data && (
						<StatCard
							title="แมนนวล"
							amount={data.manual.totalAmount}
							count={data.manual.totalCount}
							icon={<UserCheck className="h-5 w-5" />}
							colorClass="bg-emerald-500/10 text-emerald-600"
						/>
					)}
				</div>
			</CardContent>
		</Card>
	)
}

export default function TransactionStatisticsListing({
	startDate,
	endDate,
}: TransactionStatisticsListingProps) {
	const { data, isLoading, error } = useQueryTransactionStatistics({
		startDate,
		endDate,
	})

	if (isLoading) {
		return (
			<div className="flex h-32 items-center justify-center">
				<Spinner />
			</div>
		)
	}

	if (error || !data?.success || !data?.data) {
		return (
			<Card>
				<CardContent className="flex h-32 items-center justify-center">
					<p className="text-muted-foreground">ไม่พบข้อมูล</p>
				</CardContent>
			</Card>
		)
	}

	const { deposits, withdraws } = data.data

	return (
		<div className="space-y-6">
			<div className="space-y-2">
				<h2 className="text-xl font-bold tracking-tight">
					📊 สรุปธุรกรรมตามช่องทาง
				</h2>
				<p className="text-sm text-muted-foreground">
					วิเคราะห์ข้อมูลการฝาก-ถอนเงินแยกตามช่องทางการชำระเงินทั้งหมด
				</p>
			</div>

			<Separator />

			<div className="space-y-6">
				<TransactionTypeSection
					title="รายการฝากเงิน"
					data={deposits}
					icon={<ArrowDownIcon className="h-6 w-6" />}
				/>

				<TransactionTypeSection
					title="รายการถอนเงิน"
					data={withdraws}
					icon={<ArrowUpIcon className="h-6 w-6" />}
				/>
			</div>
		</div>
	)
}
